import sortBy from 'lodash-es/sortBy'
import sum from 'lodash-es/sum'

import { getObjectsSum } from '../../../utils/common'
import {
  BIRDVIEW_CHART_DEFAULT_PERIOD,
  BIRDVIEW_CHART_FLOW_POINTS,
  BIRDVIEW_CHART_FLOW_SERIES,
  BIRDVIEW_CHART_UNITS_BASE,
  BirdviewChartMetric,
  BirdviewChartType,
} from '../DashboardBirdview.constants.charts'
import {
  checkIsBirdviewActivityAgingBasis,
  checkIsBirdviewAvgTimeMetric,
  checkIsBirdviewCountMetric,
  checkIsBirdviewPerUnitsMetric,
  toBirdviewChartSection,
  toBirdviewSeriesSection,
} from '../DashboardBirdview.utils.charts'

function scaleBirdviewChartItem(item, factor) {
  return { ...item, value: Math.round(item.value * factor) }
}

// The segments are rounded first, so the label and the bar agree with them.
function scaleBirdviewStackedItem(item, factor) {
  const open = Math.round(item.open * factor)
  const overdue = Math.round(item.overdue * factor)

  return { ...item, open, overdue, value: open + overdue }
}

// TODO Both transforms run on the fixture's own fields until the backend counts
// them: units per building (IU-947) and average processing time (IU-955).
function toBirdviewMetricItem(item, metric) {
  switch (metric) {
    case BirdviewChartMetric.PerUnits:
      return {
        ...item,
        value: item.units
          ? Math.round((item.value / item.units) * BIRDVIEW_CHART_UNITS_BASE)
          : 0,
      }
    case BirdviewChartMetric.AvgTime:
      return { ...item, value: item.avgDays }
    default:
      return item
  }
}

// TODO The fixture only has the created basis; the activity one is derived by
// shifting weight into the younger buckets until `/insights` ships it.
function getBirdviewAgingBasisItems(items, basis) {
  const total = getObjectsSum(items)

  if (!checkIsBirdviewActivityAgingBasis(basis) || total === 0) {
    return items
  }

  const weights = items.map(
    (item, index) => item.value * (1 + (items.length - index) / items.length)
  )
  const weightTotal = sum(weights)
  const shares = weights.map(weight => (weight / weightTotal) * total)
  const values = shares.map(share => Math.floor(share))

  // Flooring leaves the total a few units short; they go to the buckets with
  // the largest fractions, so no bucket can turn negative.
  sortBy(
    shares.map((share, index) => ({ fraction: share - values[index], index })),
    ({ fraction }) => -fraction
  )
    .slice(0, total - sum(values))
    .forEach(({ index }) => {
      values[index] += 1
    })

  return items.map((item, index) => ({ ...item, value: values[index] }))
}

function getBirdviewBarsItems({ factor, isStacked, items, metric }) {
  if (checkIsBirdviewStackedBars({ isStacked, metric })) {
    return items.map(item => scaleBirdviewStackedItem(item, factor))
  }

  const metricItems = items.map(item => toBirdviewMetricItem(item, metric))

  return checkIsBirdviewRatioMetric(metric)
    ? metricItems
    : metricItems.map(item => scaleBirdviewChartItem(item, factor))
}

function toBirdviewFlowBucket(points) {
  return {
    ...Object.fromEntries(
      BIRDVIEW_CHART_FLOW_SERIES.map(({ dataKey }) => [
        dataKey,
        getObjectsSum(points, dataKey),
      ])
    ),
    date: points[points.length - 1].date,
  }
}

// Buckets sum the days they cover, so the period totals survive the reduction.
function bucketBirdviewChartPoints(points, count) {
  if (points.length <= count) {
    return points
  }

  const bucketSize = points.length / count

  return Array.from({ length: count }, (_, index) =>
    toBirdviewFlowBucket(
      points.slice(
        Math.round(index * bucketSize),
        Math.round((index + 1) * bucketSize)
      )
    )
  )
}

// Rates do not grow with the period; only counts are scaled by it.
export function checkIsBirdviewRatioMetric(metric) {
  return (
    checkIsBirdviewPerUnitsMetric(metric) ||
    checkIsBirdviewAvgTimeMetric(metric)
  )
}

// The open / overdue split exists for counts only.
export function checkIsBirdviewStackedBars({ isStacked, metric }) {
  return isStacked && checkIsBirdviewCountMetric(metric)
}

// TODO The period cuts or scales the fixture until `/insights` takes it.
export function getBirdviewChartPeriodData({ chart, metric, period, section }) {
  const factor = period.days / BIRDVIEW_CHART_DEFAULT_PERIOD.days

  switch (chart.type) {
    case BirdviewChartType.Line:
      return toBirdviewSeriesSection(
        bucketBirdviewChartPoints(
          section.items.slice(-period.days),
          BIRDVIEW_CHART_FLOW_POINTS
        )
      )
    case BirdviewChartType.Aging:
      return {
        items: getBirdviewAgingBasisItems(section.items, metric).map(item =>
          scaleBirdviewChartItem(item, factor)
        ),
        total: Math.round(section.total * factor),
      }
    default:
      return {
        ...toBirdviewChartSection(
          getBirdviewBarsItems({
            factor,
            isStacked: chart.isStacked,
            items: section.items,
            metric,
          })
        ),
        ...(chart.withOwnTotal
          ? { total: Math.round(section.total * factor) }
          : null),
      }
  }
}
